import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Heart } from 'lucide-react';
import { profileData } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-6xl font-bold text-white mb-6">About Dr. Samudri</h1>
            <p className="text-2xl text-[#00FFD1] font-medium">{profileData.motto}</p>
          </motion.div>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">Professional Profile</h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-[#00FFD1] mt-2" />
                  <div>
                    <p className="text-white text-opacity-70 text-sm mb-1">Full Name</p>
                    <p className="text-white text-lg">{profileData.fullName}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-[#00FFD1] mt-2" />
                  <div>
                    <p className="text-white text-opacity-70 text-sm mb-1">Current Position</p>
                    <p className="text-white text-lg font-medium">{profileData.designation}</p>
                    <p className="text-white text-opacity-85">{profileData.institution}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-[#00FFD1] mt-2" />
                  <div>
                    <p className="text-white text-opacity-70 text-sm mb-1">Previous Position</p>
                    <p className="text-white text-lg">{profileData.previousDesignation}</p>
                    <p className="text-white text-opacity-85">{profileData.previousInstitution}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-[#00FFD1] mt-2" />
                  <div>
                    <p className="text-white text-opacity-70 text-sm mb-1">Qualification</p>
                    <p className="text-white text-lg">{profileData.qualification}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-[#00FFD1] mt-2" />
                  <div>
                    <p className="text-white text-opacity-70 text-sm mb-1">Experience</p>
                    <p className="text-white text-lg">{profileData.experience}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="p-10 bg-[#121212] border border-white border-opacity-25"
            >
              <p className="text-white text-opacity-85 text-lg leading-relaxed">
                {profileData.about}
              </p>
              <p className="text-white text-opacity-85 text-lg leading-relaxed mt-6">
                Leading by example, his valuable insights ignite the spark of selfless service in his students. 
                Being highly organized and conscientious, he maintains a high standard of behavior.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Vision Section */}
      <section className="py-20 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="p-10 bg-black border border-white border-opacity-25"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#00FFD1] bg-opacity-10 flex items-center justify-center">
                  <Target className="text-[#00FFD1]" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-white">Mission</h3>
              </div>
              <p className="text-white text-opacity-85 text-lg leading-relaxed">
                {profileData.mission}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="p-10 bg-black border border-white border-opacity-25"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#00FFD1] bg-opacity-10 flex items-center justify-center">
                  <Eye className="text-[#00FFD1]" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-white">Vision</h3>
              </div>
              <p className="text-white text-opacity-85 text-lg leading-relaxed">
                {profileData.vision}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Achievements */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-5xl font-bold text-white mb-16 text-center"
          >
            Key Achievements
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Pioneering Invention",
                description: "Developed Kshara Karma technique for Tundikeri treatment, revolutionizing Ayurvedic surgical approach"
              },
              {
                title: "Research Excellence",
                description: "Groundbreaking research in Nanotechnology and Polymers in Ayurveda"
              },
              {
                title: "Surgical Training",
                description: "Hand-to-hand surgical training bringing accolades from across the country"
              },
              {
                title: "Humanitarian Leadership",
                description: "District Secretary of Indian Red Cross Society, leading disaster management and relief operations"
              },
              {
                title: "International Recognition",
                description: "Presented at conferences in Bangkok, Dubai, Singapore, and across India"
              },
              {
                title: "COVID-19 Warrior",
                description: "Relentlessly serving the needy during pandemic, upholding the Hippocratic Oath"
              }
            ].map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-8 bg-[#121212] border border-white border-opacity-25"
              >
                <div className="flex items-start gap-4">
                  <Heart className="text-[#00FFD1] flex-shrink-0" size={24} />
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">{achievement.title}</h3>
                    <p className="text-white text-opacity-70 leading-relaxed">{achievement.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;